import type { AppRouter } from 'backend/routers/app';
import type { inferRouterInputs } from '@trpc/server';

type ShouldPromptPasswordResetInput =
  inferRouterInputs<AppRouter>['session']['shouldPromptPasswordReset'];

/**
 * Cognito 認証が UserNotFound / NotAuthorized で失敗したときだけ、
 * 旧バージョン移行ユーザー向けの案内（パスワードリセット依頼）を出すべきかを API で判定する。
 * 判定 API が落ちた場合は false（通常のログイン失敗メッセージにフォールバック）。
 */
export async function resolveShouldPromptPasswordResetAfterCognitoFailure(
  error: unknown,
  options: {
    loginType: 'email' | 'employeeId';
    username: string;
    /** 社員番号ログイン時に使用。メールログイン時は空でもよい。 */
    organizationId: string | null;
    employeeNumber: string | null;
    requestShouldPromptPasswordReset: (
      input: ShouldPromptPasswordResetInput,
    ) => Promise<{ shouldPromptPasswordReset: boolean }>;
  },
): Promise<boolean> {
  if (!(error instanceof Error)) {
    return false;
  }
  if (
    error.name !== 'UserNotFoundException' &&
    error.name !== 'NotAuthorizedException'
  ) {
    return false;
  }

  try {
    if (options.loginType === 'email') {
      const response = await options.requestShouldPromptPasswordReset({
        type: 'email',
        username: options.username,
      });
      return response.shouldPromptPasswordReset;
    }

    const { organizationId, employeeNumber } = options;
    if (!organizationId || !employeeNumber) {
      return false;
    }

    const response = await options.requestShouldPromptPasswordReset({
      type: 'employeeId',
      organizationId,
      employeeNumber,
    });
    return response.shouldPromptPasswordReset;
  } catch {
    return false;
  }
}
